import React, {useState, useEffect, useContext} from 'react'
import Layout from '../../components/layout/layout'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import './ourProducts.scss'

import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import Seo, { breadcrumbJsonLd } from '../../components/Seo/Seo'
import useEntryMotion from '../../components/motion/useEntryMotion'
import { resolveProjects, projectCategories } from '../../data/projects'
import ProjectMedia from '../../components/ProjectMedia/ProjectMedia'
import ogImage from '../../images/archi/medicProductPage.jpg'
import PageBanner from '../../components/PageBanner/PageBanner'


const OurProducts = () => {
  const animateIn = useEntryMotion();
  const navigate = useNavigate();

  const { translations } = useContext(LanguageContext);


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const projects = resolveProjects(translations)

  const handleNavigateToArticle = (link) => {
    if (link.startsWith('http')) {
      window.open(link, '_blank', 'noopener');
      return;
    }
    navigate(link)
  }

  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredProjects =
    selectedCategory === 'All'
      ? projects
      : projects.filter((project) => project.category === selectedCategory);


  return (
    <Layout>
      <Seo
        title='Our Projects — ML Products We Have Built'
        description='Explore MLMind projects: MedicMinder for AI-assisted medical imaging, ArchiML for BIM automation from 2D drawings, and other machine learning products.'
        image={ogImage}
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: 'Projects', path: '/projects' },
        ])}
      />

      <div className='articles-container our-products-container'>
        <div className='articles-container-content'>

          <motion.h1
            className='our-products-main-title'
            initial={animateIn ? { opacity: 0, y: 50 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            {translations.ourProjects}
          </motion.h1>

          <motion.p
            className='our-products-intro'
            initial={animateIn ? { opacity: 0, y: 20 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            {translations.projectsIntro}
          </motion.p>

          <motion.div
            className='filter-buttons'
            initial={animateIn ? 'hidden' : false}
            animate='visible'
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } }
            }}
          >
            {projectCategories.map((category) => (
              <motion.button
                key={category}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
                }}
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.94 }}
                className={`filter-button ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {{ All: translations.uiAll, Application: translations.uiApplication }[category] || category}
                <span className='filter-count'>
                  {category === 'All' ? projects.length : projects.filter((p) => p.category === category).length}
                </span>
              </motion.button>
            ))}
          </motion.div>

          <div className='projects-grid'>

            <AnimatePresence mode='popLayout'>
              {filteredProjects.map((item, index) => (
              <motion.article
                key={item.title}
                layout
                className='project-card'
                onClick={() => handleNavigateToArticle(item.link)}
                initial={animateIn ? { opacity: 0, y: 40, scale: 0.96 } : false}
                animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] } }}
                exit={{ opacity: 0, scale: 0.92, transition: { duration: 0.3 } }}
              >
                <div className='project-card-media project-img-wrapper'>
                  <ProjectMedia project={item} className='project-card-img' loading='lazy' />
                </div>
                <div className='project-card-body'>
                  {item.tag && <span className='project-card-tag'>{item.tag}</span>}
                  <h2 className='project-card-title'>{item.title}</h2>
                  <p className='project-card-text'>{item.text}</p>
                  {item.stats.length > 0 && (
                    <dl className='project-card-stats'>
                      {item.stats.map((stat) => (
                        <div key={stat.label}><dt>{stat.value}</dt><dd>{stat.label}</dd></div>
                      ))}
                    </dl>
                  )}
                  {/* The whole card is clickable; this link keeps it reachable by keyboard */}
                  <a
                    className='project-card-link'
                    href={item.link}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleNavigateToArticle(item.link) }}
                  >
                    {translations.workOpen} <span aria-hidden='true'>→</span>
                  </a>
                </div>
              </motion.article>
              ))}
            </AnimatePresence>

          </div>

        </div>

      </div>
      <PageBanner
        eyebrow={translations.bannerProjectsEyebrow}
        title={translations.bannerProjectsTitle}
        highlight={translations.bannerProjectsHighlight}
        text={translations.bannerProjectsText}
        cta={translations.bannerProjectsCta}
        to='/services'
        shape='weave'
        colors={['#1e3a8a', '#34d399', '#a855f7']}
      />
    </Layout>
  )
}

export default OurProducts
